<template>
  <table class="table is-fullwidth is-narrow carfax-source-evidence">
    <thead>
      <tr>
        <th>Source</th>
        <th>Vehicle</th>
        <th>Details</th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="row in rows" :key="row.source" :data-test="'carfax-evidence-' + row.key">
        <td class="has-text-weight-semibold">{{ row.source }}</td>
        <td>{{ row.vehicle }}</td>
        <td class="carfax-evidence-details">{{ row.details }}</td>
      </tr>
    </tbody>
  </table>
</template>

<script>
/*
 * What each source actually said, in its own words.
 *
 * Meeting change #12 - Garrett: "I actually think we should show the Carfax, you know, the report."
 * To resolve a model mismatch he had to flip between windows; the decode screen showed the target
 * fields and none of the evidence. #14 is the same complaint sharpened: "nothing is showing me what
 * Vindicode showed, I don't have a link to the Carfax, nothing's helping me figure out."
 *
 * ⚠ A SEPARATE component rather than two more rows on CarfaxVehicleComparison, and deliberately so.
 * That component answers "which fields differ" and its spec pins the exact label set
 * ['Year','Make','Model','Trim','Style'] and the exact headers ['', 'This record', 'CARFAX'], indexing
 * cells positionally. Adding rows there would have broken those assertions for a table answering a
 * different question. This one answers "what did each source say", which is not a per-field diff.
 *
 * ⚠ Nothing here is computed from the other values. Every string is stored, read back verbatim, and
 * shown. The moment this starts deriving a display value it stops being evidence.
 */
import { isMeaningfulValue } from '@/utils/carfax_make_model_resolution'

export default {
  name: 'CarfaxSourceEvidence',
  props: {
    /*
     * The CARFAX rating, for its report heading and details line. The extracted make/model/series live
     * on the same object but are not shown here - they are what the diff table above already covers,
     * and the whole point of this table is the wording those fields lose.
     */
    carfaxRating: {
      type: Object,
      default: null
    },
    //What the VIN decoder returned, stored on the record when the VIN was decoded.
    vinDecodeName: {
      type: String,
      default: ''
    },
    vinDecodeDetails: {
      type: String,
      default: ''
    }
  },
  computed: {
    rows: function () {
      const carfax = this.carfaxRating || {}

      return [
        {
          key: 'carfax',
          source: 'CARFAX',
          vehicle: this.show(carfax.vehicleName),
          details: this.show(carfax.vehicleDetails)
        },
        {
          /*
           * ⚠ "VIN decode", matching the wording the Source column already uses elsewhere for this
           * source - see CarfaxModelReconcile. Two spellings of the same thing in one screen reads as
           * two different sources.
           */
          key: 'vin-decode',
          source: 'VIN decode',
          vehicle: this.show(this.vinDecodeName),
          details: this.show(this.vinDecodeDetails)
        }
      ]
    },
    //Nothing to show is not the same as a table of dashes - the host hides it entirely.
    hasAnything: function () {
      return this.rows.some((row) => row.vehicle !== '-' || row.details !== '-')
    }
  },
  methods: {
    /*
     * The blank convention CarfaxVehicleComparison uses: a dash, never an empty cell, and "N/A" is a
     * CARFAX sentinel rather than something the report stated.
     */
    show: function (value) {
      //⚠ Through the shared predicate, not a third hand-rolled copy. This test existed here, in
      //CarfaxSourceEvidence and twice in the utils, and they had already drifted - the server
      //rejected 'Not specified' and none of these did, so it rendered as if CARFAX had said it.
      return isMeaningfulValue(value) ? String(value).trim() : '-'
    }
  }
}
</script>

<style scoped>
  .carfax-source-evidence {
    margin-bottom: 0;
  }
  /* The details line runs long - four facts for CARFAX, a whole trim list for the decode - so it
     wraps rather than stretching the table past the modal. */
  .carfax-source-evidence td.carfax-evidence-details {
    white-space: normal;
    word-break: break-word;
  }
</style>
